import { useForm } from 'react-hook-form';
import { trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { DEFAULT_VALUES, validateTargetOptionParam, type FormData, type TimeFrame } from '../../form-types.ts';
import { TargetTypeSelectControlled } from './target-type-select/TargetTypeSelectControlled.tsx';

const TIME_FRAMES: TimeFrame[] = ['7', '30', '90'];
const PARAM_SCHEMA = { targetType: 'scalar', targetId: 'scalar' } as const;

type Props = {
  onSubmit: (data: FormData) => void;
  /** Close the modal without saving */
  closeModal: () => void;
};

/**
 * {@link ExportLogsForm} collects inputs from the user and requests a CSV to be exported.
 */
export function ExportLogsForm({ onSubmit: modalOnSubmit, closeModal }: Props) {
  // We default the `targetType` to what was filtered on in `AuditLogsPage`
  const [params] = useUrlParams(PARAM_SCHEMA);
  const { targetType: targetTypeParam, targetId } = params;
  const validatedtargetTypeParam = validateTargetOptionParam(targetTypeParam);
  const { handleSubmit, setError, formState, control } = useForm<FormData>({
    defaultValues: {
      targetType: validatedtargetTypeParam,
      targetId,
      timeFrame: DEFAULT_VALUES.timeFrame,
    },
  });
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit((data) => {
    try {
      modalOnSubmit(data);
    } catch (error) {
      if (error instanceof ValidationError) {
        setError('root.global', { message: 'Something went wrong, please try again' });
      } else {
        trackError(error);
        setError('root.global', { message: "We couldn't prepare the logs for download, please try again" });
      }
    }
  });

  return (
    <>
      <ModalBody>
        <form method="post" id="export-logs-form" onSubmit={onSubmit}>
          <Spacer direction="vertical" size="wide" className="mb-16">
            {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
            {targetId ? null : (
              <TargetTypeSelectControlled control={control} errorMessage={errors.targetType?.message} />
            )}
            <SelectControlled
              label="Time frame"
              name="timeFrame"
              control={control}
              renderValue={(timeFrame) => {
                return `Last ${timeFrame} days`;
              }}
              errorMessage={errors.timeFrame?.message}
              renderOptions={({ onChange }) => (
                <>
                  {TIME_FRAMES.map((days) => (
                    <SelectItem key={days} value={days} onClick={() => onChange(days)}>
                      {`Last ${days} days`}
                    </SelectItem>
                  ))}
                </>
              )}
            />
          </Spacer>
        </form>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form="export-logs-form" disabled={isSubmitting}>
              Export CSV
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={closeModal}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </>
  );
}
